import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatMoneyWithSymbol } from '@helpers/index';
import { Modal } from '@components/index';
import { Button } from '@shared/components/button/Button';
import H2Heading from '@shared/components/headings/H2Heading';
import Paragraph from '@shared/components/paragraph/Paragraph';
import { getCurrencyCodeSelector } from '@modules/subscriptions/core/selectors';
import { useShopPromo } from './useShopPromo';

const ShopPromoModal: React.FC = () => {
  const intl = useIntl();
  const { open, product, close } = useShopPromo();
  const navigate = useNavigate();
  const currencySymbol = useSelector(getCurrencyCodeSelector);

  const cover = product?.images?.[0];
  const firstVariant = product?.variants?.find((v) => v.availableForSale);

  const handleExplore = () => {
    close();
    navigate('/mitbestellen');
  };

  return (
    <Modal isOpen={open && Boolean(product)} handleClose={close}>
      {product && (
        <div className="shop-promo">
          <div className="shop-promo__media">
            <span className="shop-promo__badge">Aus dem Shop</span>
            {cover ? (
              <img
                className="shop-promo__image"
                src={cover.src}
                alt={cover.altText ?? product.title}
              />
            ) : (
              <div className="shop-promo__media-placeholder" aria-hidden />
            )}
          </div>

          <div className="shop-promo__body">
            <span className="shop-promo__eyebrow">
              <FormattedMessage id="SHOP.PROMO.EYEBROW" />
            </span>

            <H2Heading
              noMarginBottom
              className="!text-[2.4rem] !leading-[3rem] md:!text-[2.8rem] md:!leading-[3.4rem]">
              <strong>{product.title}</strong>
            </H2Heading>

            {product.productType && <span className="shop-promo__tag">{product.productType}</span>}

            {product.description && (
              <Paragraph
                lightColor
                className="shop-promo__description !text-[1.4rem] !leading-[2rem]">
                {firstSentence(product.description)}
              </Paragraph>
            )}

            {firstVariant && (
              <div className="shop-promo__price-row">
                <span className="shop-promo__price-label">
                  <FormattedMessage id="SHOP.PRICE_FROM" />
                </span>
                <span className="shop-promo__price-value">
                  {formatPrice(firstVariant.price, currencySymbol, intl.locale)}
                </span>
              </div>
            )}

            <div className="shop-promo__actions">
              <Button variant="outline" onClick={close}>
                <FormattedMessage id="GLOBAL.LATER" />
              </Button>
              <Button variant="primary" onClick={handleExplore}>
                <FormattedMessage id="SHOP.PROMO.EXPLORE" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

const firstSentence = (raw: string): string => {
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  const match = trimmed.match(/^[^.!?]+[.!?]/);
  if (match) return match[0];
  if (trimmed.length > 140) return `${trimmed.slice(0, 137).trimEnd()}…`;
  return trimmed;
};

// Keeps the old contract of returning '' for a non-numeric price rather than
// formatting a zero; the symbol placement comes from the shared helper.
const formatPrice = (raw: string | undefined, currencySymbol: string, locale: string): string => {
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return '';

  return formatMoneyWithSymbol(numeric, currencySymbol, locale);
};

export default ShopPromoModal;
